import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Chip,
    Divider,
    Grid2,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { useGetVenue, addVenue, editVenue } from '@openapi/venues/venues';
import { useGetShows } from '@openapi/shows/shows';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import { useTranslation } from 'react-i18next';
import { ExpandMore } from '@mui/icons-material';
import LanguageIcon from '@mui/icons-material/Language';
import { slugifier } from '../../helpers/slugifier';
import OnboardingGuide from './components/OnboardingGuide';
import { useCapabilities } from '../../hooks/useCapabilities';

function EditVenuePage({ newVenue }) {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageVenues = can(CAPABILITIES.VENUES_MANAGE);
    const readOnly = !newVenue && !canManageVenues;
    const { venueId } = useParams();
    const { showModal } = useModals();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [venue, setVenue] = useState(null);
    const { notifyError, notifySuccess } = useNotify();

    const defaultVenue = {
        name: t('venues.defaultName'),
        slug: t('venues.defaultSlug'),
        nameI18n: {},
        site: t('venues.defaultSite'),
        availableLanguages: [],
        allowedSplitPaymentAmount: 0,
        invoiceRemitToName: '',
    };

    const {
        data: venueData,
        isLoading: venueLoading,
        error: _venueError,
    } = useGetVenue(Number(venueId), {
        query: {
            enabled: !newVenue && !!venueId,
            onError: (err) => {
                navigate('/venues');
                notifyError(err);
            },
        },
    });

    const loadVenue = useCallback(() => {
        if (venueData) {
            setVenue({
                id: venueData.id,
                name: venueData.name,
                nameI18n: venueData.nameI18n,
                slug: venueData.slug,
                site: venueData.site,
                customFields: venueData.customFields,
                availableLanguages: venueData.availableLanguages,
                allowedSplitPaymentAmount: venueData.allowedSplitPaymentAmount,
                invoiceRemitToName: venueData.invoiceRemitToName ?? '',
            });
        }
    }, [venueData]);

    useEffect(() => {
        if (!newVenue) {
            loadVenue();
            setLoading(venueLoading);
        } else {
            setLoading(false);
        }
    }, [venueId, loadVenue, newVenue, venueLoading]);

    const onEdit = (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editVenue(Number(venueId), {
                        name: values.name,
                        nameI18n: values.nameI18n,
                        slug: values.slug,
                        site: values.site,
                        availableLanguages: values.availableLanguages,
                        allowedSplitPaymentAmount: values.allowedSplitPaymentAmount,
                        invoiceRemitToName: values.invoiceRemitToName?.trim() || null,
                    });
                    navigate('/venues');
                    notifySuccess(t('venues.notifications.updated'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const onSubmit = async (values) => {
        if (!newVenue) {
            return onEdit(values);
        }
        try {
            await addVenue({
                name: values.name,
                nameI18n: values.nameI18n,
                slug: values.slug,
                site: values.site,
                availableLanguages: values.availableLanguages,
                allowedSplitPaymentAmount: values.allowedSplitPaymentAmount,
                invoiceRemitToName: values.invoiceRemitToName?.trim() || null,
            });
            navigate('/venues');
            notifySuccess(t('venues.notifications.added'));
        } catch (error) {
            notifyError(error);
        }
    };

    const { data: shows, isLoading: loadingShows } = useGetShows(
        { pageSize: 10000 },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    if (loading || loadingShows) {
        return true;
    }

    const validateSchema = Yup.object().shape({
        name: Yup.string().required(t('common.required')),
        slug: Yup.string().required(t('common.required')),
        allowedSplitPaymentAmount: Yup.number()
            .min(0, t('common.positiveNumber'))
            .required(t('common.required')),
    });

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={venue || { ...defaultVenue }}
            validationSchema={validateSchema}
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                <Form>
                    <PageWithTitle title={newVenue ? t('venues.newVenueTitle') : `${venue.name}`}>
                        <Grid2 size={{ xs: 12 }}>
                            <BlockWithTitle title={t('venues.title')}>
                                <Grid2 size={{ xs: readOnly ? 12 : 10, sm: readOnly ? 12 : 11 }}>
                                    <TextField
                                        fullWidth
                                        disabled={readOnly}
                                        label={t('venues.columns.name')}
                                        value={values.name}
                                        onChange={(ev) => setFieldValue('name', ev.target.value)}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name ? errors.name : ''}
                                    />
                                </Grid2>
                                {!readOnly && (
                                    <Grid2 size={{ xs: 2, sm: 1 }} sx={{ alignContent: 'center' }}>
                                        <Tooltip title={t('common.translations')}>
                                            <IconButton
                                                onClick={() => {
                                                    showModal(MODAL_TYPE.TRANSLATIONS, {
                                                        translations: values.nameI18n,
                                                        setTranslations: (translations) =>
                                                            setFieldValue('nameI18n', translations),
                                                        item: 'venues',
                                                        column: 'name',
                                                        venueId: values.id,
                                                        originalString: values.name,
                                                    });
                                                }}
                                            >
                                                <LanguageIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid2>
                                )}
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        disabled={readOnly}
                                        label={t('venues.columns.slug')}
                                        value={values.slug}
                                        onChange={(ev) =>
                                            setFieldValue('slug', slugifier(ev.target.value))
                                        }
                                        error={touched.slug && Boolean(errors.slug)}
                                        helperText={touched.slug ? errors.slug : ''}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        disabled={readOnly}
                                        label={t('venues.columns.site')}
                                        value={values.site}
                                        onChange={(ev) => setFieldValue('site', ev.target.value)}
                                        error={touched.site && Boolean(errors.site)}
                                        helperText={touched.site ? errors.site : ''}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        disabled={readOnly}
                                        label={t('venues.columns.invoiceRemitToName')}
                                        helperText={t('venues.invoiceRemitToNameHelp')}
                                        value={values.invoiceRemitToName ?? ''}
                                        onChange={(ev) =>
                                            setFieldValue('invoiceRemitToName', ev.target.value)
                                        }
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        disabled={readOnly}
                                        type="number"
                                        label={t('venues.columns.allowedSplitPaymentAmount')}
                                        value={values.allowedSplitPaymentAmount}
                                        onChange={(ev) =>
                                            setFieldValue(
                                                'allowedSplitPaymentAmount',
                                                parseInt(ev.target.value, 10),
                                            )
                                        }
                                        error={
                                            touched.allowedSplitPaymentAmount &&
                                            Boolean(errors.allowedSplitPaymentAmount)
                                        }
                                        helperText={
                                            touched.allowedSplitPaymentAmount &&
                                            errors.allowedSplitPaymentAmount
                                                ? errors.allowedSplitPaymentAmount
                                                : t('venues.allowedSplitPaymentAmountHelp')
                                        }
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        disabled={readOnly}
                                        options={[]}
                                        value={values.availableLanguages}
                                        onChange={(_, newValue) => {
                                            setFieldValue('availableLanguages', newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('venues.columns.availableLanguages')}
                                                placeholder={t(
                                                    'common.freesoloAutocompletePlaceholder',
                                                )}
                                                fullWidth
                                                style={{ minWidth: '200px' }}
                                            />
                                        )}
                                        getOptionLabel={(option) => {
                                            return `${option}`;
                                        }}
                                    />
                                </Grid2>
                                {!readOnly && (
                                    <>
                                        <Grid2 size={{ xs: 6, md: 2 }}>
                                            <CommonButton
                                                fullWidth
                                                onClick={() => navigate('/venues')}
                                                label={t('common.cancel')}
                                                buttonType="secondary"
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 6, md: 2 }}>
                                            <CommonButton
                                                fullWidth
                                                disabled={isSubmitting}
                                                type="submit"
                                                label={t('common.save')}
                                                buttonType="primary"
                                            />
                                        </Grid2>
                                    </>
                                )}
                            </BlockWithTitle>
                        </Grid2>
                        <Grid2 size={{ xs: 12 }}>
                            <OnboardingGuide
                                shows={
                                    shows?.shows?.filter((e) => `${e.venueId}` === `${venueId}`) ||
                                    []
                                }
                                venueSlug={venueData?.slug}
                            />
                        </Grid2>
                    </PageWithTitle>
                    <Grid2 size={{ xs: 12 }} sx={{ mt: 2, textAlign: 'left' }}>
                        <Accordion defaultExpanded={false}>
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography variant="subtitle1">
                                        {t('tickettypes.columns.customFields')}
                                    </Typography>
                                </Grid2>
                            </AccordionSummary>
                            <AccordionDetails>
                                {values.customFields?.map((field, idx) => (
                                    <React.Fragment key={idx}>
                                        <Grid2 container spacing={1}>
                                            <Grid2 size={{ xs: 12, md: 2 }}>
                                                <Typography variant="body2">
                                                    <b>
                                                        {t(
                                                            'tickettypes.customFieldsAttributes.type',
                                                        )}
                                                    </b>
                                                    : {field.type}
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, md: 3 }}>
                                                <Typography variant="body2">
                                                    <b>
                                                        {t(
                                                            'tickettypes.customFieldsAttributes.name',
                                                        )}
                                                    </b>
                                                    : {field.name}
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, md: 5 }}>
                                                <Typography variant="body2" component="div">
                                                    <b>
                                                        {t(
                                                            'tickettypes.customFieldsAttributes.description',
                                                        )}
                                                    </b>
                                                    :{' '}
                                                    {Array.isArray(field.description)
                                                        ? field.description.map((desc, idx) => (
                                                              <Chip
                                                                  key={idx}
                                                                  label={desc}
                                                                  sx={{ mr: 1 }}
                                                              />
                                                          ))
                                                        : field.description}
                                                </Typography>
                                            </Grid2>
                                            <Grid2 size={{ xs: 12, md: 2 }}>
                                                <Typography variant="body2">
                                                    <b>
                                                        {t(
                                                            'tickettypes.customFieldsAttributes.required',
                                                        )}
                                                    </b>
                                                    :{' '}
                                                    {field.required
                                                        ? t('common.yes')
                                                        : t('common.no')}
                                                </Typography>
                                            </Grid2>
                                        </Grid2>
                                        <Divider sx={{ m: 1 }} />
                                    </React.Fragment>
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    </Grid2>
                </Form>
            )}
        </Formik>
    );
}

export default EditVenuePage;
